import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useWatch } from 'react-hook-form';
import translations from './AlgorithmTargetFormModal.translations.json';
import { Flex } from 'antd';
import { FormField } from '../../../common/form';
import { AlgorithmTargetItemDimensionIndicatorSelect } from './AlgorithmTargetItemDimensionIndicatorSelect';
import styled from 'styled-components';
import { AddRemoveButtons } from '../../../common/components';
import { AlgorithmTargetMapTypeSourceField } from './AlgorithmTargetMapTypeSourceField';
import { AlgorithmTargetValidFormDataType } from './schema/types';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmType } from '../schema/zAlgorithmType';
import { useAlgorithmTargetRuleOptions } from './ruleOptions/useAlgorithmTargetRuleOptions';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-modal',
);
addTranslations(translations);

interface AlgorithmTargetMapTypeFieldProps {
  algorithmTypeCode: AlgorithmType;
  index: number;
  isLast: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  areDbSources: boolean;
}

export function AlgorithmTargetMapTypeField({
  algorithmTypeCode,
  index,
  isLast,
  onAdd,
  onRemove,
  sources,
  mappings,
  areDbSources,
}: AlgorithmTargetMapTypeFieldProps) {
  const { t } = useTranslation();
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const name = [
    `rules.${index}.dimensionIndicators`,
    `rules.${index}.sourceItem`,
  ] as const;
  const [dimensionIndicators, sourceItem] = useWatch<
    AlgorithmTargetValidFormDataType,
    typeof name
  >({ name });

  const { sourceOptions, targetOptions } = useAlgorithmTargetRuleOptions({
    algorithmTypeCode,
    dataObjectVersion: dataObjectDescriptionVersion,
    sources,
    mappings,
    currentSourceValue: sourceItem,
    currentTargetValue: dimensionIndicators,
  });

  return (
    <Wrapper vertical gap="middle">
      <FormField
        preview={{
          previewOnly: true,
        }}
        size="small"
        name={`rules.${index}.ruleCode`}
        label={t('ruleCode.label')}
      />
      <AlgorithmTargetMapTypeSourceField
        index={index}
        sources={sources}
        mappings={mappings}
        options={sourceOptions}
        areDbSources={areDbSources}
      />
      <AlgorithmTargetItemDimensionIndicatorSelect
        index={index}
        options={targetOptions}
      />
      <AddRemoveButtons
        id={`algorithmTargetMapType-control-${index}`}
        onClickAdd={isLast ? onAdd : undefined}
        onClickRemove={() => onRemove(index)}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Flex)`
  display: grid;
  grid-template-columns: 40px 1fr 1fr 60px;
`;
